import { type LucideIcon } from 'lucide-react';
import {
  ClipboardList,
  Brain,
  Target,
  PenLine,
  SquareGantt,
  Glasses,
  MonitorPlay,
  FileText,
  GraduationCap,
  Dumbbell,
  Library,
  Users2,
  LayoutDashboard,
  Users,
  PenSquare,
  BarChart3,
  Shield,
  Settings,
  Wrench,
  BookOpen,
  Scale,
  Timer,
  TrendingUp,
  Layers,
  Flag,
  MessageCircle,
  MessageSquare,
  LifeBuoy,
} from 'lucide-react';

export type NavVisibility = 'all' | 'staff' | 'admin';

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  onHoverPrefetch?: () => void;
  /**
   * Hidden on live (jdsimplified.com) AND on studicata. Visible on dev/admin only.
   * Studicata records public demos from studicata.jdsimplified.com so any item not
   * yet ready for external eyes (e.g. Coursework) carries this flag.
   * Note: applies at the item level (filterDevOnly) — group-level visibility may have a
   * separate role-based override (e.g. NextGen group is shown to admin/partner on live,
   * but Coursework still gets filtered out from the group's items).
   */
  devOnly?: boolean;
  visibility?: NavVisibility;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  /** If set, clicking the group label navigates here instead of toggling collapse. */
  href?: string;
  isActive?: (pathname: string) => boolean;
  /** Show a small activity dot next to the group label (e.g., unread notifications). */
  showDot?: boolean;
}

export type NavEntry =
  | { type: 'item'; item: NavItem }
  | { type: 'group'; group: NavGroup }
  | { type: 'separator' };

/** Action Plan is rendered separately at the top, outside the scrollable nav entries. */
export function getActionPlanItem(): NavItem {
  return {
    name: 'Action Plan',
    href: '/dashboard',
    icon: ClipboardList,
    isActive: (p) => p.startsWith('/dashboard'),
  };
}

/** Returns the ordered nav entries rendered below Ask JDS. */
export function getNavigationEntries(options: {
  isAskJDS: boolean;
  /** True when on jdsimplified.com (live prod). Drives the soft-launch gate for NextGen. */
  isJDSimplified?: boolean;
  /** True when on admin.jdsimplified.com domain. */
  isAdmin?: boolean;
  /** True when on studicata.jdsimplified.com domain. */
  isStudicata?: boolean;
  /** True when the logged-in user has admin role (profiles.role='admin' or Firebase claim). */
  isAdminUser?: boolean;
  /** True when the logged-in user has partner role (profiles.role='partner'). */
  isPartnerUser?: boolean;
  prefetchIssueSpotter?: () => void;
}): NavEntry[] {
  // Filter helper: drop items flagged devOnly when we're on live (jdsimplified.com)
  // OR on studicata (public demo domain). Visible on dev/admin only.
  const filterDevOnly = <T extends { devOnly?: boolean }>(items: T[]): T[] =>
    options.isJDSimplified || options.isStudicata ? items.filter((i) => !i.devOnly) : items;

  // NextGen sidebar group is shown when:
  //   - we're NOT on live (dev/admin/studicata always see the group), OR
  //   - the logged-in user has admin or partner role on live (soft-launch override).
  // Coursework sub-item carries devOnly: true so it stays hidden on live even for
  // admin/partner — the feature doesn't ship until next year.
  const showNextGen =
    !options.isJDSimplified || options.isAdminUser || options.isPartnerUser;

  // Studicata portal: show everything EXCEPT community
  // (community is dev-only and not ready for external partner eyes)
  return [
    {
      type: 'item',
      item: {
        name: 'Notes',
        href: '/notes',
        icon: FileText,
        isActive: (p) => p.startsWith('/notes'),
      },
    },
    {
      type: 'group',
      group: {
        id: 'practice',
        label: 'Practice',
        icon: Dumbbell,
        items: [
          {
            name: 'Study',
            href: '/flashcards/study',
            icon: Brain,
            isActive: (p) => p.startsWith('/flashcards'),
          },
          {
            name: 'Issue Spotter',
            href: '/issue-spotter',
            icon: Target,
            isActive: (p) => p.startsWith('/issue-spotter'),
            onHoverPrefetch: options.prefetchIssueSpotter,
          },
          {
            name: 'Essay Coach',
            href: '/essay-coach',
            icon: PenLine,
            isActive: (p) => p.startsWith('/essay-coach'),
          },
        ],
      },
    },
    {
      type: 'group',
      group: {
        id: 'library',
        label: 'Library',
        icon: Library,
        items: [
          {
            name: 'Outlines',
            href: '/outlines/library',
            icon: SquareGantt,
            isActive: (p) => p.startsWith('/outlines'),
          },
          {
            name: 'Case Law',
            href: '/cases',
            icon: Glasses,
            isActive: (p) => p.startsWith('/cases'),
          },
          {
            name: 'Courses',
            href: '/courses',
            icon: MonitorPlay,
            isActive: (p) => p === '/courses' || p.startsWith('/course/'),
          },
        ],
      },
    },
    ...((options.isAskJDS || options.isAdmin) && !options.isStudicata
      ? [
          {
            type: 'group' as const,
            group: {
              id: 'community',
              label: 'Community',
              icon: Users2,
              href: '/community',
              isActive: (p: string) => p.startsWith('/community'),
              items: [
                {
                  name: 'Discussions',
                  href: '/community',
                  icon: MessageCircle,
                  isActive: (p: string) => p.startsWith('/community') && !p.startsWith('/community/groups') && !p.startsWith('/community/support'),
                },
                {
                  name: 'Study Groups',
                  href: '/community/groups',
                  icon: Users,
                  isActive: (p: string) => p.startsWith('/community/groups'),
                  devOnly: true,
                },
                {
                  name: 'Support',
                  href: '/community/support',
                  icon: MessageSquare,
                  isActive: (p: string) => p.startsWith('/community/support'),
                  devOnly: true,
                },
              ],
            },
          },
        ]
      : []),
    ...(showNextGen
      ? [
          {
            type: 'group' as const,
            group: {
              id: 'nextgen',
              label: 'NextGen Bar Prep',
              icon: GraduationCap,
              href: '/nextgen/skills/practice',
              isActive: (p: string) => p.startsWith('/nextgen'),
              items: filterDevOnly([
                {
                  name: 'Coursework',
                  href: '/nextgen/coursework',
                  icon: BookOpen,
                  isActive: (p: string) => p.startsWith('/nextgen/coursework'),
                  devOnly: true,
                },
                {
                  name: 'Skills',
                  href: '/nextgen/skills/practice',
                  icon: Target,
                  isActive: (p: string) => p.startsWith('/nextgen/skills'),
                },
                {
                  name: 'Simulations',
                  href: '/nextgen/simulations',
                  icon: Timer,
                  isActive: (p: string) => p.startsWith('/nextgen/simulations'),
                },
                {
                  name: 'Progress',
                  href: '/nextgen/progress',
                  icon: TrendingUp,
                  isActive: (p: string) => p.startsWith('/nextgen/progress'),
                },
                {
                  name: 'Coach',
                  href: '/nextgen/coach',
                  icon: MessageCircle,
                  isActive: (p: string) => p.startsWith('/nextgen/coach'),
                },
              ]),
            },
          },
        ]
      : []),
  ];
}

/** Returns admin-specific nav entries (shown only on admin domain). */
export function getAdminNavigationEntries(options: {
  isAdmin: boolean;
  isStaff: boolean;
}): NavEntry[] {
  const items: NavItem[] = [
    {
      name: 'Admin Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
      isActive: (p) => p === '/admin/dashboard',
      visibility: 'all',
    },
    {
      name: 'Courses',
      href: '/admin/courses',
      icon: BookOpen,
      isActive: (p) => p.startsWith('/admin/courses'),
      visibility: 'all',
    },
    {
      name: 'Flashcards',
      href: '/admin/flashcards',
      icon: Library,
      isActive: (p) => p.startsWith('/admin/flashcards'),
      visibility: 'staff',
    },
    {
      name: 'Outlines',
      href: '/admin/outlines',
      icon: FileText,
      isActive: (p) => p.startsWith('/admin/outlines'),
      visibility: 'staff',
    },
    {
      name: 'Case Law',
      href: '/admin/cases',
      icon: Scale,
      isActive: (p) => p.startsWith('/admin/cases'),
      visibility: 'staff',
    },
    {
      name: 'Blog Management',
      href: '/admin/blog',
      icon: PenSquare,
      isActive: (p) => p.startsWith('/admin/blog'),
      visibility: 'staff',
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      isActive: (p) => p.startsWith('/admin/users'),
      visibility: 'admin',
    },
    {
      name: 'Web Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      isActive: (p) => p === '/admin/analytics',
      visibility: 'admin',
    },
    {
      name: 'Error Logs',
      href: '/admin/error-logs',
      icon: Shield,
      isActive: (p) => p === '/admin/error-logs',
      visibility: 'admin',
    },
    {
      name: 'Content Health',
      href: '/admin/content-health',
      icon: Layers,
      isActive: (p) => p === '/admin/content-health',
      visibility: 'admin',
    },
    {
      name: 'Moderation',
      href: '/admin/moderation',
      icon: Flag,
      isActive: (p) => p === '/admin/moderation',
      visibility: 'staff',
    },
    {
      name: 'Support',
      href: '/admin/support',
      icon: LifeBuoy,
      isActive: (p) => p.startsWith('/admin/support'),
      visibility: 'staff',
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      isActive: (p) => p === '/admin/settings',
      visibility: 'admin',
    },
  ];

  // Filter by role visibility
  const filtered = items.filter((item) => {
    const vis = item.visibility ?? 'all';
    if (vis === 'all') return true;
    if (vis === 'staff') return options.isStaff;
    if (vis === 'admin') return options.isAdmin;
    return false;
  });

  return [
    {
      type: 'group',
      group: {
        id: 'admin',
        label: 'Admin',
        icon: Wrench,
        items: filtered,
      },
    },
  ];
}
